import React from 'react';

import {
  FOLLOWUP_CHIP_PALETTE,
  INTENT_LABELS,
  ROLE_CHIP_PALETTE,
  ROLE_LABELS,
} from '../../constants/visitActionPending';

/**
 * ActionPendingChip — renders the small pill on a visit card showing
 * who currently has an action pending on the visit, or — when only
 * `doctor_followup_pending_at` is set — the "Resume" follow-up reason.
 *
 * Placement: between the patient identity and the status timestamp on
 * the queue card. Tooltip carries comment + age.
 *
 * Reads the visit row directly — no API call. Renders nothing when
 * neither overlay nor follow-up is set.
 */
function _ageLabel(iso) {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  const ms = Date.now() - t;
  const min = Math.max(0, Math.round(ms / 60000));
  if (min < 1) return 'now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

export default function ActionPendingChip({ visit, compact = false }) {
  if (!visit) return null;

  const overlayRole = visit.action_pending_with_role;
  const overlayIntent = visit.action_pending_intent;
  const overlayName = visit.action_pending_with_name;
  const overlayAt = visit.action_pending_set_at;

  const followupAt = visit.doctor_followup_pending_at;
  const followupReason = visit.doctor_followup_reason;

  if (!overlayRole && !followupAt) return null;

  // Overlay takes precedence when both are set (shouldn't happen in
  // practice, but defensive).
  if (overlayRole) {
    const palette = ROLE_CHIP_PALETTE[overlayRole] || ROLE_CHIP_PALETTE.RECEPTIONIST;
    const intentLabel = INTENT_LABELS[overlayIntent] || overlayIntent || '';
    const roleLabel = ROLE_LABELS[overlayRole] || overlayRole;
    const tooltip = [
      `Action with ${roleLabel}${overlayName ? ` (${overlayName})` : ''}`,
      intentLabel,
      _ageLabel(overlayAt),
    ].filter(Boolean).join(' · ');

    return (
      <span
        title={tooltip}
        className={[
          'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium',
          palette.bg, palette.border, palette.text,
          compact ? '' : 'whitespace-nowrap',
        ].join(' ')}
      >
        <span aria-hidden="true">⌛</span>
        {compact ? roleLabel : `${roleLabel}${overlayName ? ` · ${overlayName}` : ''}`}
      </span>
    );
  }

  // Doctor follow-up flag.
  const palette = FOLLOWUP_CHIP_PALETTE;
  const reasonText = INTENT_LABELS[followupReason] || followupReason || 'Resume';
  return (
    <span
      title={`${reasonText} · ${_ageLabel(followupAt)}`}
      className={[
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium',
        palette.bg, palette.border, palette.text,
      ].join(' ')}
    >
      <span aria-hidden="true">↩</span>
      Resume
    </span>
  );
}
